import { useState, useEffect } from "react";
import { clientsAPI, servicesAPI, invoicesAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, FileText, Download, Trash2, Loader2, Receipt, RefreshCw, Eye, Search, X } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/utils";

export default function InvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    
    const [formData, setFormData] = useState({
        client_id: "",
        items: [{ description: "", quantity: 1, price: 0 }],
        notes: ""
    });

    useEffect(() => {
        loadData();
    }, []);

    // Filter invoices when search query changes
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredInvoices(invoices);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = invoices.filter(inv => 
                inv.invoice_number?.toLowerCase().includes(query) ||
                inv.client_name?.toLowerCase().includes(query) ||
                inv.client_phone?.toLowerCase().includes(query)
            );
            setFilteredInvoices(filtered);
        }
    }, [searchQuery, invoices]);

    const extractData = (response, key) => {
        const data = response?.data;
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data[key])) return data[key];
        if (Array.isArray(data.data)) return data.data;
        return [];
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [invoicesRes, clientsRes, servicesRes] = await Promise.all([
                invoicesAPI.getAll(),
                clientsAPI.getAll(),
                servicesAPI.getAll()
            ]);
            
            const invoicesData = extractData(invoicesRes, 'invoices');
            setInvoices(invoicesData);
            setFilteredInvoices(invoicesData);
            setClients(extractData(clientsRes, 'clients'));
            setServices(extractData(servicesRes, 'services'));
        } catch (error) {
            console.error("Load error:", error);
            if (error.response?.status !== 404) {
                toast.error("Failed to load data");
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async (invoiceId) => {
        setPreviewLoading(true);
        setShowPreviewDialog(true);
        try {
            const response = await invoicesAPI.getDetail(invoiceId);
            const invoiceData = response.data?.invoice || response.data?.data || response.data;
            setPreviewInvoice(invoiceData);
        } catch (error) {
            toast.error("Failed to load invoice details");
            setShowPreviewDialog(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { description: "", quantity: 1, price: 0 }]
        });
    };

    const removeItem = (index) => {
        if (formData.items.length === 1) return;
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = field === 'quantity' || field === 'price' ? parseFloat(value) || 0 : value;
        setFormData({ ...formData, items: newItems });
    };

    const addServiceAsItem = (serviceId) => {
        if (!serviceId) return;
        const service = services.find(s => s.id === serviceId);
        if (service) {
            setFormData({
                ...formData,
                items: [...formData.items, { 
                    description: service.name, 
                    quantity: 1, 
                    price: parseFloat(service.price) || 0
                }]
            });
            toast.success(`Added ${service.name}`);
        }
    };

    const calculateTotal = () => {
        return formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    };

    const handleGenerate = async () => {
        if (!formData.client_id) {
            toast.error("Please select a client");
            return;
        }
        
        const validItems = formData.items.filter(item => item.description);
        if (validItems.length === 0) {
            toast.error("Please add at least one item");
            return;
        }
        
        setGenerating(true);
        try {
            const response = await invoicesAPI.generate({
                client_id: formData.client_id,
                items: validItems,
                notes: formData.notes
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success("Invoice generated and downloaded!");
            setShowCreateDialog(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to generate invoice");
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async (invoiceId, invoiceNumber) => {
        setDownloading(invoiceId);
        try {
            const response = await invoicesAPI.download(invoiceId);
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success("Invoice downloaded!");
        } catch (error) {
            toast.error("Failed to download invoice");
        } finally {
            setDownloading(null);
        }
    };

    const resetForm = () => {
        setFormData({
            client_id: "",
            items: [{ description: "", quantity: 1, price: 0 }],
            notes: ""
        });
    };

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Invoices</h1>
                    <p className="text-muted-foreground">Generate and manage client invoices</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadData} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button 
                        onClick={() => setShowCreateDialog(true)}
                        className="rounded-xl text-white"
                        style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)' }}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Invoice
                    </Button>
                </div>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Search by invoice #, client name or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10"
                />
                {searchQuery && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                        onClick={() => setSearchQuery("")}
                    >
                        <X className="w-4 h-4" />
                    </Button>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-border/40 shadow-card">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Receipt className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{invoices.length}</p>
                                <p className="text-xs text-muted-foreground">Total Invoices</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-border/40 shadow-card">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-green-600">
                                    {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.total || 0), 0))}
                                </p>
                                <p className="text-xs text-muted-foreground">Total Invoiced</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Invoices */}
            <Card className="border-border/40 shadow-card">
                <CardHeader>
                    <CardTitle className="font-heading">
                        {searchQuery ? `Search Results` : 'Recent Invoices'}
                    </CardTitle>
                    <CardDescription>
                        {searchQuery 
                            ? `${filteredInvoices.length} invoice${filteredInvoices.length !== 1 ? 's' : ''} found`
                            : `${invoices.length} invoices generated`
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            {searchQuery ? (
                                <>
                                    <p>No invoices found matching "{searchQuery}"</p>
                                    <Button 
                                        variant="link" 
                                        onClick={() => setSearchQuery("")}
                                        className="mt-2"
                                    >
                                        Clear search
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <p>No invoices yet</p>
                                    <Button 
                                        variant="link" 
                                        onClick={() => setShowCreateDialog(true)}
                                        className="mt-2"
                                    >
                                        Create your first invoice
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Invoice #</TableHead>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Items</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredInvoices.map((invoice) => (
                                        <TableRow key={invoice.id}>
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {invoice.invoice_number}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-medium">{invoice.client_name}</TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {formatDate(invoice.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">
                                                    {invoice.items?.length || 0} items
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-semibold text-primary">
                                                {formatCurrency(invoice.total)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handlePreview(invoice.id)}
                                                        title="Preview"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                                                        disabled={downloading === invoice.id}
                                                        title="Download"
                                                    >
                                                        {downloading === invoice.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Download className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create Invoice Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
                setShowCreateDialog(open);
                if (!open) resetForm();
            }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Create Invoice</DialogTitle>
                        <DialogDescription>Generate a professional PDF invoice for your client</DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                        <div className="space-y-2">
                            <Label>Client *</Label>
                            <Select 
                                value={formData.client_id} 
                                onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map((client) => (
                                        <SelectItem key={client.id} value={client.id}>
                                            {client.name} - {client.phone}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {services.length > 0 && (
                            <div className="space-y-2">
                                <Label>Quick Add Service</Label>
                                <Select onValueChange={addServiceAsItem}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a service to add" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {services.map((service) => (
                                            <SelectItem key={service.id} value={service.id}>
                                                {service.name} - {formatCurrency(service.price)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Items</Label>
                            <div className="space-y-3">
                                {formData.items.map((item, index) => (
                                    <div key={index} className="flex gap-2 items-start p-3 rounded-lg bg-secondary/30">
                                        <Input
                                            placeholder="Description"
                                            value={item.description}
                                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                                            className="flex-1"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Qty"
                                            value={item.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                            className="w-20"
                                            min="1"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Price"
                                            value={item.price}
                                            onChange={(e) => updateItem(index, 'price', e.target.value)}
                                            className="w-28"
                                            min="0"
                                            step="0.01"
                                        />
                                        <Button 
                                            variant="ghost" 
                                            size="icon"
                                            onClick={() => removeItem(index)}
                                            disabled={formData.items.length === 1}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <Button variant="outline" size="sm" onClick={addItem} className="mt-2">
                                <Plus className="w-4 h-4 mr-1" /> Add Item
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Payment terms, thank you message, etc."
                                rows={3}
                            />
                        </div>

                        <div className="flex justify-between items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                            <span className="font-medium">Total Amount</span>
                            <span className="text-3xl font-bold text-primary">
                                {formatCurrency(calculateTotal())}
                            </span>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleGenerate} 
                                disabled={generating}
                                className="min-w-[160px] text-white"
                                style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)' }}
                            >
                                {generating ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                                ) : (
                                    <><Download className="w-4 h-4 mr-2" />Generate PDF</>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Preview Invoice Dialog */}
            <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {previewLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : previewInvoice ? (
                        <>
                            <DialogHeader className="border-b pb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <DialogTitle className="font-heading text-2xl">
                                            {previewInvoice.business_name || 'TRIMY'}
                                        </DialogTitle>
                                        <DialogDescription>
                                            {previewInvoice.business_phone}
                                            {previewInvoice.business_address && ` • ${previewInvoice.business_address}`}
                                        </DialogDescription>
                                    </div>
                                    <div className="text-right">
                                        <Badge className="text-lg px-3 py-1 bg-cyan-500">INVOICE</Badge>
                                    </div>
                                </div>
                            </DialogHeader>

                            <div className="space-y-6 py-4">
                                {/* Invoice Details */}
                                <div className="flex justify-between">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase font-semibold mb-1">Bill To</p>
                                        <p className="font-semibold">{previewInvoice.client_name}</p>
                                        <p className="text-sm text-muted-foreground">{previewInvoice.client_phone}</p>
                                        {previewInvoice.client_email && (
                                            <p className="text-sm text-muted-foreground">{previewInvoice.client_email}</p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm"><span className="text-muted-foreground">Invoice #:</span> <span className="font-mono">{previewInvoice.invoice_number}</span></p>
                                        <p className="text-sm"><span className="text-muted-foreground">Date:</span> {formatDate(previewInvoice.created_at)}</p>
                                        <Badge variant="outline" className="mt-1 text-green-600 border-green-600">
                                            {previewInvoice.status?.toUpperCase() || 'GENERATED'}
                                        </Badge>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <div className="rounded-lg border overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-900">
                                                <TableHead className="text-white">#</TableHead>
                                                <TableHead className="text-white">Description</TableHead>
                                                <TableHead className="text-white text-center">Qty</TableHead>
                                                <TableHead className="text-white text-right">Price</TableHead>
                                                <TableHead className="text-white text-right">Amount</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {previewInvoice.items?.map((item, index) => (
                                                <TableRow key={index} className={index % 2 === 1 ? 'bg-slate-50' : ''}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{item.description}</TableCell>
                                                    <TableCell className="text-center">{item.quantity}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(item.price)}</TableCell>
                                                    <TableCell className="text-right font-medium">
                                                        {formatCurrency(item.quantity * item.price)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Totals */}
                                <div className="flex justify-end">
                                    <div className="w-64 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Subtotal:</span>
                                            <span>{formatCurrency(previewInvoice.subtotal)}</span>
                                        </div>
                                        {previewInvoice.tax > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Tax:</span>
                                                <span>{formatCurrency(previewInvoice.tax)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between pt-2 border-t">
                                            <span className="font-semibold">Total:</span>
                                            <span className="text-xl font-bold text-primary">
                                                {formatCurrency(previewInvoice.total)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Notes */}
                                {previewInvoice.notes && (
                                    <div className="pt-4 border-t">
                                        <p className="text-xs text-muted-foreground uppercase font-semibold mb-1">Notes</p>
                                        <p className="text-sm text-muted-foreground">{previewInvoice.notes}</p>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                                    Close
                                </Button>
                                <Button 
                                    onClick={() => {
                                        handleDownload(previewInvoice.id, previewInvoice.invoice_number);
                                        setShowPreviewDialog(false);
                                    }}
                                    className="text-white"
                                    style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)' }}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download PDF
                                </Button>
                            </div>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}